import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtErr, fmtQty } from "../../api";
import { useAuth } from "../../context/AuthContext";
import { Plus, Copy, Pencil, Trash2 } from "lucide-react";
import StatusBadge from "../../components/StatusBadge";
import MfgTabs from "../../components/MfgTabs";

export default function BOMs() {
  const [boms, setBoms] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const canEdit = ["admin", "production"].includes(user?.role);

  const load = () => api.get("/boms").then((r) => setBoms(r.data)).catch((e) => toast.error(fmtErr(e)));
  useEffect(() => { load(); }, []);

  const duplicate = async (id) => {
    try {
      await api.post(`/boms/${id}/duplicate`);
      toast.success("BOM duplicated (inactive)");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete BOM "${b.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/boms/${b.id}`);
      toast.success("BOM deleted");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const toggleActive = async (b) => {
    try {
      await api.put(`/boms/${b.id}`, { active: !b.active });
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div data-testid="boms-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-3xl font-extrabold text-[#002B5D] tracking-tight">Manufacturing</h1>
        {canEdit && (
          <Link to="/manufacturing/boms/new" data-testid="new-bom-button"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md font-heading font-bold text-sm text-white" style={{ backgroundColor: "#002B5D" }}>
            <Plus className="w-4 h-4" /> New BOM
          </Link>
        )}
      </div>
      <MfgTabs />
      <div className="sk-card overflow-x-auto mt-4">
        <table className="w-full sk-table" data-testid="bom-table">
          <thead><tr><th>BOM</th><th>Finished Product</th><th>Version</th><th>Components</th><th>Per</th><th>Status</th>{canEdit && <th></th>}</tr></thead>
          <tbody>
            {!boms && <tr><td colSpan={7} className="text-center text-slate-400">Loading...</td></tr>}
            {boms?.length === 0 && <tr><td colSpan={7} className="text-center text-slate-400">No BOMs yet</td></tr>}
            {boms?.map((b) => (
              <tr key={b.id} data-testid={`bom-row-${b.id}`}>
                <td className="font-semibold text-[#002B5D]">{b.name}</td>
                <td>{b.product_name} <span className="font-mono text-xs text-slate-400">{b.product_code}</span></td>
                <td className="font-mono">v{b.version}</td>
                <td>
                  <div className="text-xs text-slate-500 max-w-[260px] whitespace-normal break-words leading-tight"
                    title={b.components.map((c) => `${c.product_name} ${fmtQty(c.qty)} ${c.uom}`).join(" · ")}>
                    {b.components.map((c) => `${c.product_name} ${fmtQty(c.qty)} ${c.uom}`).join(" · ")}
                  </div>
                </td>
                <td className="font-mono text-xs">{fmtQty(b.basis_qty)} {b.basis_uom}</td>
                <td><StatusBadge status={b.active ? "Passed" : "Cancelled"} testid={`bom-status-${b.id}`} /></td>
                {canEdit && (
                  <td>
                    <div className="flex gap-1 justify-end">
                      <button data-testid={`bom-edit-${b.id}`} title="Edit" onClick={() => navigate(`/manufacturing/boms/${b.id}/edit`)} className="p-2 rounded hover:bg-slate-100 text-slate-500"><Pencil className="w-4 h-4" /></button>
                      <button data-testid={`bom-duplicate-${b.id}`} title="Duplicate" onClick={() => duplicate(b.id)} className="p-2 rounded hover:bg-slate-100 text-slate-500"><Copy className="w-4 h-4" /></button>
                      <button data-testid={`bom-delete-${b.id}`} title="Delete" onClick={() => remove(b)} className="p-2 rounded hover:bg-rose-50 text-rose-500"><Trash2 className="w-4 h-4" /></button>
                      <button data-testid={`bom-toggle-${b.id}`} onClick={() => toggleActive(b)}
                        className={`px-2 py-1 rounded text-xs font-semibold ${b.active ? "text-rose-600 hover:bg-rose-50" : "text-green-700 hover:bg-green-50"}`}>
                        {b.active ? "Deactivate" : "Activate"}
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
